/**
 * Supporting-document rules for payout statement approve/dispute.
 *
 * The backend requires a document on BOTH endpoints and validates it twice:
 * the file extension and the sniffed MIME type must each be on the allowlist,
 * so renaming a .txt to .pdf is rejected server-side. These constants mirror
 * that contract so the UI can fail fast with a specific message.
 */

/** Maximum accepted document size, in MB (backend limit). */
export const PAYOUT_DOCUMENT_MAX_SIZE_MB = 15

export const PAYOUT_DOCUMENT_MAX_SIZE_BYTES = PAYOUT_DOCUMENT_MAX_SIZE_MB * 1024 * 1024

/** Allowed file extensions (lowercase, leading dot). */
export const PAYOUT_DOCUMENT_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.webp'] as const

/** Value for the file input's `accept` attribute. */
export const PAYOUT_DOCUMENT_ACCEPT = PAYOUT_DOCUMENT_EXTENSIONS.join(',')

/** Allowed MIME types, paired with the extensions above. */
export const PAYOUT_DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
] as const

/** Max length of the `notes` (approve) and `reason` (dispute) fields. */
export const PAYOUT_TEXT_MAX_LENGTH = 2000

/**
 * Validation failures, named after the backend error codes so the UI can use a
 * single message table for both client-side and server-side rejections.
 */
export type PayoutDocumentErrorCode =
  | 'document_required'
  | 'document_empty'
  | 'document_too_large'
  | 'document_unsupported_type'

function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.')
  return dot === -1 ? '' : fileName.slice(dot).toLowerCase()
}

/**
 * Check a picked file against the backend's document rules.
 *
 * @returns the matching error code, or `null` when the file looks acceptable.
 *
 * The MIME check is skipped when the browser reports an empty `type` (it cannot
 * always infer one); the backend sniffs the real type and is the authority.
 */
export function validatePayoutDocument(file: File | null | undefined): PayoutDocumentErrorCode | null {
  if (!file) return 'document_required'
  if (file.size === 0) return 'document_empty'
  if (file.size > PAYOUT_DOCUMENT_MAX_SIZE_BYTES) return 'document_too_large'

  const extensionAllowed = (PAYOUT_DOCUMENT_EXTENSIONS as readonly string[]).includes(extensionOf(file.name))
  const mimeAllowed =
    file.type === '' || (PAYOUT_DOCUMENT_MIME_TYPES as readonly string[]).includes(file.type.toLowerCase())

  if (!extensionAllowed || !mimeAllowed) return 'document_unsupported_type'

  return null
}
